import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, Calendar } from 'lucide-react';
import { getNews } from '../lib/api';
import { CardShell, EmptyState, LoadingScreen, PageHero, staggerContainer, staggerItem } from '../components/common/BrandElements';

const NewsPage = () => {
  const { t, isRTL } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getNews();
        setNews(res.data.filter(n => n.is_active));
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="news-page">
      <PageHero
        kicker={t('أخبار الشركة', 'Company News')}
        title={t('آخر الأخبار', 'Latest News')}
        description={t('تحديثات شركات المجموعة ونشاطاتها ضمن عرض بصري بسيط وواضح.', 'Updates from the group companies, presented with a simple and clear visual system.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {news.length === 0 ? (
            <EmptyState title={t('لا توجد أخبار حالياً', 'No news available')} />
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {news.map((item) => (
                <motion.div key={item.id} variants={staggerItem}>
                  <Link to={`/news/${item.slug}`} className="group block h-full" data-testid={`news-card-${item.slug}`}>
                    <CardShell className="h-full">
                      <div className="relative aspect-[4/3] overflow-hidden bg-raad-mist">
                        <img
                          src={item.image_url || '/assets/raad-identity-hero.png'}
                          alt={t(item.title_ar, item.title_en)}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute end-4 top-4 rounded-full bg-white/90 p-3 text-raad-charcoal shadow-soft transition-colors group-hover:bg-raad-teal group-hover:text-white">
                          <ArrowUpRight className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-raad-graphite/55">
                          <Calendar className="h-4 w-4 text-raad-teal" />
                          {new Date(item.published_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                        </div>
                        <h2 className="text-2xl font-black text-raad-charcoal group-hover:text-raad-teal line-clamp-2">
                          {t(item.title_ar, item.title_en)}
                        </h2>
                        {item.tags?.length > 0 && (
                          <div className="mt-5 flex flex-wrap gap-2">
                            {item.tags.slice(0, 3).map((tag, i) => (
                              <span key={`${tag}-${i}`} className="rounded-full bg-raad-mist px-3 py-1 text-xs font-bold text-raad-graphite/60">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardShell>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
